import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createOnboarding, updateOnboarding, uploadSliderImages, uploadGalleryImages, getOnboarding, uploadOwnerDocuments } from '../../services/onboardingService';
import {
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Chip,
  Rating,
  Box,
  Typography,
  Modal,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Sparkles, Hotel, Send } from 'lucide-react';
import {amenityServices} from '../../services/ametieServices';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './OnboardingForm.css';


const steps = ['Basic', 'Gallery', 'Address', 'Owner Details', 'Additional'];

// Nepal location data
const nepalLocationData = {
  "country": "Nepal",
  "states": [
    {
      "state": "Koshi Province",
      "capital": "Biratnagar",
      "cities": ["Biratnagar", "Dharan", "Itahari", "Damak", "Birtamod", "Bhojpur", "Okhaldhunga"]
    },
    {
      "state": "Madhesh Province",
      "capital": "Janakpur",
      "cities": ["Janakpur", "Birgunj", "Kalaiya", "Malangwa", "Gaur", "Siraha", "Rajbiraj"]
    },
    {
      "state": "Bagmati Province",
      "capital": "Hetauda",
      "cities": ["Kathmandu", "Lalitpur", "Bhaktapur", "Hetauda", "Dhulikhel", "Bharatpur", "Chitwan"]
    },
    {
      "state": "Gandaki Province",
      "capital": "Pokhara",
      "cities": ["Pokhara", "Gorkha", "Tanahun", "Baglung", "Lamjung", "Syangja"]
    },
    {
      "state": "Lumbini Province",
      "capital": "Deukhuri (Dang)",
      "cities": ["Butwal", "Bhairahawa", "Taulihawa", "Kapilvastu", "Nepalgunj", "Dang"]
    },
    {
      "state": "Karnali Province",
      "capital": "Birendranagar (Surkhet)",
      "cities": ["Surkhet", "Jumla", "Dailekh", "Kalikot", "Mugu", "Dolpa"]
    },
    {
      "state": "Sudurpashchim Province",
      "capital": "Dhangadhi",
      "cities": ["Dhangadhi", "Attariya", "Dadeldhura", "Mahendranagar", "Baitadi", "Doti"]
    }
  ]
};

const OnboardingForm = ({ existingData, onSuccess }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingHotel, setLoadingHotel] = useState(false);

  let user = JSON.parse(localStorage.getItem('user'));
  const userId = user._id;
  console.log(userId);


  const initialHotelState = {
    name: '',
    user: userId,
    image: '',
    gallery: [],
    location: { type: 'Point', coordinates: [] },
    address: { street: '', city: '', state: '', country: '', placeId: '' },
    description: '',
    rulesPolicies: '',
    amenities: [],
    tags: [],
    short_desc: '',
    landmark: '',
    check_in: '',
    check_out: '',
    status: false,
    rating: 0,
    hotelOwnerDetails: {
      ownerName: '',
      ownerIdProof: '',
      hotelRegistrationDoc: '',
      ownerMobile: '',
      ownerEmail: ''
    }
  };

  const [hotel, setHotel] = useState(existingData || initialHotelState);

  const [amenitiesList, setAmenitiesList] = useState([]);
  const [loadingAmenities, setLoadingAmenities] = useState(false);
  const [amenitiesDropdownOpen, setAmenitiesDropdownOpen] = useState(false);
  console.log("Amenities List:", amenitiesList);

  const fetchAmenities = async () => {
    setLoadingAmenities(true);
    try {
      const amenitiesData = await amenityServices.getAllAmenities();

      if (amenitiesData?.data?.records?.length) {
        const formattedAmenities = amenitiesData.data.records
          .map((amenity) => ({
            id: amenity._id || amenity.id,
            name: amenity.name || amenity.title,
            icon: amenity.icon || null,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setAmenitiesList(formattedAmenities);
        console.log(
          "Formatted amenities (alphabetically sorted):",
          formattedAmenities
        );
      } else {
        setAmenitiesList([]);
      }
    } catch (error) {
      console.error("Error fetching amenities:", error);
      setAmenitiesList([]);
    } finally {
      setLoadingAmenities(false);
    }
  };

  useEffect(() => {
    fetchAmenities();
  }, []);

  // Load hotel data when editing
  useEffect(() => {
    const loadHotelData = async () => {
      if (editId) {
        setLoadingHotel(true);
        try {
          const response = await getOnboarding(editId);
          const hotelData = response.data?.record || response.record || response.data;

          if (hotelData) {
            // Normalize amenities to just IDs and ensure hotelOwnerDetails exists
            const normalizedHotel = {
              ...hotelData,
              amenities: hotelData.amenities?.map(a => a.id || a._id || a) || [],
              hotelOwnerDetails: hotelData.hotelOwnerDetails || {
                ownerName: '',
                ownerIdProof: '',
                hotelRegistrationDoc: '',
                ownerMobile: '',
                ownerEmail: ''
              }
            };
            setHotel(normalizedHotel);
          }
        } catch (error) {
          console.error('Failed to load hotel data:', error);
          openSnack({ type: 'error', message: 'Failed to load hotel data' });
        } finally {
          setLoadingHotel(false);
        }
      }
    };

    loadHotelData();
  }, [editId]);

  const [imageFile, setImageFile] = useState(null);
  const [ownerIdFile, setOwnerIdFile] = useState(null);
  const [hotelRegDocFile, setHotelRegDocFile] = useState(null);
  const [uploadingOwnerId, setUploadingOwnerId] = useState(false);
  const [uploadingRegDoc, setUploadingRegDoc] = useState(false);

  // Gallery category state
  const [currentGalleryCategory, setCurrentGalleryCategory] = useState({
    type: 'Image',
    category: '',
    url: []
  });
  const [uploadingGallery, setUploadingGallery] = useState(false);

  // Image modal state
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const handleNext = () => setActiveStep(prev => prev + 1);
  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleChange = e => {
    const { name, value } = e.target;
    setHotel(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = e => {
    const { name, value } = e.target;
    setHotel(prev => ({ ...prev, address: { ...prev.address, [name]: value } }));
  };

  const handleOwnerDetailsChange = e => {
    const { name, value } = e.target;
    setHotel(prev => ({
      ...prev,
      hotelOwnerDetails: { ...prev.hotelOwnerDetails, [name]: value }
    }));
  };

  const handleFileChange = async e => {
    const file = e.target.files[0];
    if (!file) return;

    setImageFile(file);
    setLoading(true);

    try {
      // Upload to AWS S3
      const response = await uploadSliderImages([file]);

      if (response.data && response.data.length > 0) {
        // Set S3 URL instead of base64
        const s3Url = response.data[0].location;
        setHotel(prev => ({ ...prev, image: s3Url }));
        openSnack({ type: 'success', message: '📸 Hotel main image uploaded successfully!' });
      }
    } catch (error) {
      console.error('Image upload failed:', error);
      openSnack({ type: 'error', message: '❌ Failed to upload image. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleGalleryChange = async e => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check if category is entered
    if (!currentGalleryCategory.category.trim()) {
      openSnack({ type: 'error', message: '⚠️ Please enter gallery category name first!' });
      // Clear the file input
      e.target.value = '';
      return;
    }

    setUploadingGallery(true);

    try {
      // Upload all gallery images to AWS S3
      const response = await uploadGalleryImages(files);

      if (response.data && response.data.length > 0) {
        // Get S3 URLs from response
        const s3Urls = response.data.map(file => file.location);

        // Add new category with URLs to gallery
        const newCategory = {
          type: currentGalleryCategory.type,
          category: currentGalleryCategory.category,
          url: s3Urls,
          status: false // default status
        };

        setHotel(prev => ({
          ...prev,
          gallery: [...prev.gallery, newCategory]
        }));

        // Reset current category
        setCurrentGalleryCategory({
          type: 'Image',
          category: '',
          url: []
        });

        // Clear file input
        e.target.value = '';

        openSnack({ type: 'success', message: `🖼️ ${files.length} image(s) added to "${newCategory.category}" gallery!` });
      }
    } catch (error) {
      console.error('Gallery upload failed:', error);
      openSnack({ type: 'error', message: '❌ Failed to upload gallery images. Please try again.' });
      // Clear the file input on error
      e.target.value = '';
    } finally {
      setUploadingGallery(false);
    }
  };

  const removeGalleryCategory = (index) => {
    setHotel(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index)
    }));
    openSnack({ type: 'success', message: 'Gallery category removed' });
  };

  const handleAmenitiesChange = event => {
    const {
      target: { value },
    } = event;
    setHotel(prev => ({
      ...prev,
      amenities: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const openSnack = ({ type, message }) => {
    if (type === 'success') {
      toast.success(message, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else if (type === 'error') {
      toast.error(message, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else {
      toast.info(message, {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  // Handle image modal
  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
  };

  // Remove image from gallery category
  const removeImageFromGallery = (categoryIndex, imageIndex) => {
    const updatedGallery = [...hotel.gallery];
    updatedGallery[categoryIndex].url.splice(imageIndex, 1);

    // If no images left in category, remove the category
    if (updatedGallery[categoryIndex].url.length === 0) {
      updatedGallery.splice(categoryIndex, 1);
    }

    setHotel(prev => ({ ...prev, gallery: updatedGallery }));
    toast.success('Image removed successfully');
  };

  // Handle Owner ID Proof upload
  const handleOwnerIdUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;

    setOwnerIdFile(file);
    setUploadingOwnerId(true);

    try {
      const response = await uploadOwnerDocuments([file]);

      if (response.data && response.data.length > 0) {
        const s3Url = response.data[0].location;
        setHotel(prev => ({
          ...prev,
          hotelOwnerDetails: { ...prev.hotelOwnerDetails, ownerIdProof: s3Url }
        }));
        openSnack({ type: 'success', message: '✅ Owner ID proof uploaded successfully!' });
      }
    } catch (error) {
      console.error('Owner ID upload failed:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Upload failed';
      openSnack({ type: 'error', message: `❌ Failed to upload owner ID: ${errorMsg}` });
    } finally {
      setUploadingOwnerId(false);
    }
  };

  // Handle Hotel Registration Document upload
  const handleHotelRegDocUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;

    setHotelRegDocFile(file);
    setUploadingRegDoc(true);

    try {
      const response = await uploadOwnerDocuments([file]);

      if (response.data && response.data.length > 0) {
        const s3Url = response.data[0].location;
        setHotel(prev => ({
          ...prev,
          hotelOwnerDetails: { ...prev.hotelOwnerDetails, hotelRegistrationDoc: s3Url }
        }));
        openSnack({ type: 'success', message: '✅ Hotel registration document uploaded successfully!' });
      }
    } catch (error) {
      console.error('Hotel registration document upload failed:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Upload failed';
      openSnack({ type: 'error', message: `❌ Failed to upload registration document: ${errorMsg}` });
    } finally {
      setUploadingRegDoc(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // STEP validations
    if (activeStep === 0 && !hotel.name.trim()) {
      openSnack({ type: 'error', message: '⚠️ Hotel name is required!' });
      return;
    }
    if (activeStep === 0 && !imageFile && !hotel.image) {
      openSnack({ type: 'error', message: '⚠️ Please upload hotel main image!' });
      return;
    }
    if (activeStep === 3 && !hotel.hotelOwnerDetails.ownerName.trim()) {
      openSnack({ type: 'error', message: '⚠️ Owner name is required!' });
      return;
    }
    if (activeStep === 3 && !hotel.hotelOwnerDetails.ownerMobile.trim()) {
      openSnack({ type: 'error', message: '⚠️ Owner mobile number is required!' });
      return;
    }
    if (activeStep === 3 && !hotel.hotelOwnerDetails.ownerEmail.trim()) {
      openSnack({ type: 'error', message: '⚠️ Owner email is required!' });
      return;
    }
    if (activeStep < 4) {
      handleNext();
      return;
    }
    if (activeStep === 4 && !hotel.description.trim()) {
      openSnack({ type: 'error', message: '⚠️ Hotel description is required!' });
      return;
    }

    setLoading(true);

    let h = { ...hotel };

    // Fetch coordinates via Google Geocode API
    const fullAddress = `${h.address.street}, ${h.address.city}, ${h.address.state}, ${h.address.country}`;
    if (h.address.city && h.address.country) {
      try {
        const res = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            fullAddress
          )}&key=AIzaSyA-eQYNqnyeOS4jA7neQscDXLRhARyRJiw`
        );
        const data = await res.json();

        if (data.status === 'OK' && data.results.length > 0) {
          const loc = data.results[0].geometry.location;
          h.location = { type: 'Point', coordinates: [loc.lng, loc.lat] };
          h.address.placeId = data.results[0].place_id;
        } else {
          openSnack({ type: 'error', message: 'Invalid address. Please correct it.' });
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error('Geocode failed', err);
        openSnack({ type: 'error', message: 'Failed to fetch coordinates' });
        setLoading(false);
        return;
      }
    }

    // Normalize amenities - ensure we send IDs only
    h.amenities = h.amenities.map(a => (typeof a === 'object' ? (a.id || a._id) : a));

    try {
      if (!h._id) {
        const response = await createOnboarding(h);
        const createdHotelId = response.data?.record?._id || response.data?._id || response.record?._id;
        setLoading(false);
        openSnack({
          type: 'success',
          message: `🎉 Hotel "${h.name}" has been added successfully! Redirecting to room-management...`
        });

        // Reset form after successful submission
        setHotel(initialHotelState);
        setImageFile(null);
        setOwnerIdFile(null);
        setHotelRegDocFile(null);
        setCurrentGalleryCategory({ type: 'Image', category: '', url: [] });
        setActiveStep(0);

        // Navigate to room-management with hotel ID after 2 seconds
        setTimeout(() => {
          navigate(`/room-management?hotel=${createdHotelId}`);
        }, 2000);
      } else {
        await updateOnboarding(h._id, h);
        setLoading(false);
        openSnack({
          type: 'success',
          message: `✅ Hotel "${h.name}" has been updated successfully! Redirecting to room-management...`
        });

        // Navigate to room-management with hotel ID after update
        setTimeout(() => {
          navigate(`/room-management?hotel=${h._id}`);
        }, 2000);
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err);
      setLoading(false);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to submit form';
      openSnack({
        type: 'error',
        message: `❌ Error: ${errorMessage}. Please try again.`
      });
    }
  };

  if (loadingHotel) {
    return (
      <div className="onboarding-modern-wrapper">
        <div className="max-w-3xl mx-auto modern-glass-card p-6">
          <div className="modern-loading" style={{ position: 'relative', minHeight: '400px' }}>
            <div className="spinner-gradient"></div>
            <Typography variant="h6" style={{ marginTop: '1.5rem', color: '#667eea', fontWeight: 600 }}>
              Loading hotel data...
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-modern-wrapper">
      <div className="max-w-3xl mx-auto modern-glass-card p-8" style={{ position: 'relative' }}>
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="gradient-text-animated text-5xl font-bold mb-2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
            {editId ? (
              <>
                <Sparkles size={40} style={{ color: '#667eea' }} />
                <span>Edit Your Hotel</span>
              </>
            ) : (
              <>
                <Hotel size={40} style={{ color: '#667eea' }} />
                <span>Add New Hotel</span>
              </>
            )}
          </h2>
          <Typography variant="subtitle1" style={{ color: '#64748b', fontSize: '1.1rem' }}>
            {editId ? 'Update your hotel information below' : 'Fill in the details to list your amazing property'}
          </Typography>
        </div>

        {/* Stepper */}
        <div className="modern-stepper mb-8">
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </div>

        {/* Loading overlay when submitting */}
        {loading && (
          <div className="modern-loading">
            <div className="spinner-gradient"></div>
            <Typography variant="h6" style={{ marginTop: '1.5rem', color: '#667eea', fontWeight: 600 }}>
              {editId ? 'Updating hotel...' : 'Creating hotel...'}
            </Typography>
          </div>
        )}

        <form onSubmit={handleSubmit} className="form-section-animated">
        {/* STEP 1 - Basic Info */}
        {activeStep === 0 && (
          <div className="space-y-4">
            <div className="enhanced-textfield">
              <TextField label="Hotel Name" name="name" value={hotel.name} onChange={handleChange} fullWidth required />
            </div>
            <div className="enhanced-textfield">
              <TextField label="Short Description" name="short_desc" value={hotel.short_desc} onChange={handleChange} fullWidth />
            </div>
            <div className="enhanced-textfield">
              <TextField
                label="Tags (comma separated)"
                value={hotel.tags.join(',')}
                onChange={e => setHotel(prev => ({ ...prev, tags: e.target.value.split(',') }))}
                fullWidth
              />
            </div>
            <FormControl fullWidth>
              <InputLabel>Amenities</InputLabel>
              <Select
                multiple
                value={hotel.amenities}
                onChange={handleAmenitiesChange}
                disabled={loadingAmenities}
                open={amenitiesDropdownOpen}
                onOpen={() => setAmenitiesDropdownOpen(true)}
                onClose={() => setAmenitiesDropdownOpen(false)}
                renderValue={selected => (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {selected.map(val => {
                      const a = amenitiesList.find(a => a.id === val);
                      return <Chip key={val} label={a?.name || val} className="animated-chip" />;
                    })}
                  </div>
                )}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                    },
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
              >
                <div className="amenities-dropdown-header">
                  <Typography variant="body2" style={{ fontWeight: 600, color: '#667eea' }}>
                    Select Amenities
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setAmenitiesDropdownOpen(false)}
                    className="amenities-dropdown-close-btn"
                  >
                    <CloseIcon style={{ fontSize: '16px' }} />
                  </IconButton>
                </div>
                {loadingAmenities ? (
                  <MenuItem disabled>Loading amenities...</MenuItem>
                ) : amenitiesList.length === 0 ? (
                  <MenuItem disabled>No amenities available</MenuItem>
                ) : (
                  amenitiesList.map(a => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <div>
              <label className="font-semibold block mb-2">Hotel Main Image</label>
              <div className="mb-2">
                <input
                  id="main-image-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="main-image-input">
                  <Button
                    variant="contained"
                    component="span"
                    disabled={loading}
                    style={{
                      backgroundColor: loading ? '#cccccc' : '#667eea',
                      color: 'white',
                      textTransform: 'none',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? '📤 Uploading...' : '📁 Choose File'}
                  </Button>
                </label>
              </div>
              {hotel.image ? (
                <div className="relative inline-block group">
                  <img
                    src={hotel.image}
                    alt="preview"
                    className="rounded-md object-cover cursor-pointer"
                    style={{ width: '45px', height: '45px' }}
                    onClick={() => openImageModal(hotel.image)}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      setHotel(prev => ({ ...prev, image: '' }));
                      setImageFile(null);
                      toast.success('Hotel image removed');
                    }}
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      background: '#ef4444',
                      color: 'white',
                      width: '20px',
                      height: '20px',
                      opacity: 0,
                      transition: 'opacity 0.3s',
                    }}
                    className="group-hover:opacity-100"
                  >
                    <CloseIcon style={{ fontSize: '12px' }} />
                  </IconButton>
                </div>
              ) : (
                <p className="text-sm text-gray-500 mt-1">No image</p>
              )}
            </div>
          </div>
        )}

        {/* STEP 2 - Gallery Information */}
        {activeStep === 1 && (
          <div className="space-y-4">
            <div className="modern-gallery-section border p-4 rounded-lg">
              <label className="font-semibold block mb-3 text-lg" style={{ color: '#667eea' }}>
                📸 Gallery Images (Categorized)
              </label>

              {/* Add new category form */}
              <div className="space-y-3 bg-gray-50 p-3 rounded mb-4">
                <div className="enhanced-textfield">
                  <TextField
                    label="Category Name"
                    placeholder="e.g., Bar and Restaurant, Swimming Pool, Rooms"
                    value={currentGalleryCategory.category}
                    onChange={(e) => setCurrentGalleryCategory(prev => ({ ...prev, category: e.target.value }))}
                    fullWidth
                    size="small"
                  />
                </div>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={currentGalleryCategory.type}
                    onChange={(e) => setCurrentGalleryCategory(prev => ({ ...prev, type: e.target.value }))}
                    label="Type"
                  >
                    <MenuItem value="Image">Image</MenuItem>
                    <MenuItem value="Video">Video</MenuItem>
                  </Select>
                </FormControl>
                <div>
                  <input
                    id="gallery-file-input"
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleGalleryChange}
                    disabled={uploadingGallery}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="gallery-file-input">
                    <Button
                      variant="contained"
                      component="span"
                      disabled={uploadingGallery}
                      style={{
                        backgroundColor: uploadingGallery ? '#cccccc' : '#667eea',
                        color: 'white',
                        textTransform: 'none',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        cursor: uploadingGallery ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {uploadingGallery ? '📤 Uploading...' : '📁 Choose Images'}
                    </Button>
                  </label>
                  {uploadingGallery && <p className="text-sm text-blue-600 mt-2">⏳ Uploading images to server...</p>}
                  {/* <>{!currentGalleryCategory.category.trim() && (
                    <p className="text-xs text-amber-600 mt-2 font-medium">⚠️ Please enter category name above before uploading images</p>
                  )}</> */}
                </div>
              </div>

              {/* Display existing gallery categories */}
              <div className="space-y-3">
                {hotel.gallery.map((galleryItem, index) => (
                  <div key={index} className="category-card-modern border rounded-lg p-3 bg-white">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <h4 className="font-semibold text-sm">{galleryItem.category}</h4>
                        <p className="text-xs text-gray-500">Type: {galleryItem.type} | Images: {galleryItem.url?.length || 0} | Status: {galleryItem.status ? '✓ Active' : '✗ Inactive'}</p>
                      </div>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => removeGalleryCategory(index)}
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {galleryItem.url?.map((imgUrl, imgIndex) => (
                        <div key={imgIndex} className="image-preview-modern relative group">
                          <img
                            src={imgUrl}
                            alt={`${galleryItem.category}-${imgIndex}`}
                            className="object-cover rounded-md border cursor-pointer"
                            style={{ width: '45px', height: '45px' }}
                            onClick={() => openImageModal(imgUrl)}
                          />
                          <IconButton
                            size="small"
                            onClick={() => removeImageFromGallery(index, imgIndex)}
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-8px',
                              background: '#ef4444',
                              color: 'white',
                              width: '20px',
                              height: '20px',
                              opacity: 0,
                              transition: 'opacity 0.3s',
                            }}
                            className="group-hover:opacity-100"
                          >
                            <CloseIcon style={{ fontSize: '12px' }} />
                          </IconButton>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {hotel.gallery.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No gallery categories added yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 - Address & Location */}
        {activeStep === 2 && (
          <div className="space-y-4">
            {/* Street Field */}
            <div className="enhanced-textfield">
              <TextField
                label="Street"
                name="street"
                value={hotel.address.street}
                onChange={handleAddressChange}
                fullWidth
              />
            </div>

            {/* State Dropdown */}
            <div className="enhanced-textfield">
              <FormControl fullWidth>
                <InputLabel>State</InputLabel>
                <Select
                  name="state"
                  value={hotel.address.state}
                  onChange={(e) => {
                    const selectedState = e.target.value;
                    setHotel((prev) => ({
                      ...prev,
                      address: {
                        ...prev.address,
                        state: selectedState,
                        city: "", // reset city when state changes
                        country: nepalLocationData.country,
                      },
                    }));
                  }}
                >
                  {nepalLocationData.states.map((s, i) => (
                    <MenuItem key={i} value={s.state}>
                      {s.state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* City Dropdown */}
            <div className="enhanced-textfield">
              <FormControl fullWidth disabled={!hotel.address.state}>
                <InputLabel>City</InputLabel>
                <Select
                  name="city"
                  value={hotel.address.city}
                  onChange={handleAddressChange}
                >
                  {hotel.address.state
                    ? nepalLocationData.states
                        .find((s) => s.state === hotel.address.state)
                        ?.cities.map((city, idx) => (
                          <MenuItem key={idx} value={city}>
                            {city}
                          </MenuItem>
                        ))
                    : []}
                </Select>
              </FormControl>
            </div>

            {/* Country Field (read-only) */}
            <div className="enhanced-textfield">
              <TextField
                label="Country"
                name="country"
                value={hotel.address.country || nepalLocationData.country}
                fullWidth
                disabled
              />
            </div>

            {/* Landmark Field */}
            <div className="enhanced-textfield">
              <TextField
                label="Landmark"
                name="landmark"
                value={hotel.landmark}
                onChange={handleChange}
                fullWidth
              />
            </div>
          </div>
        )}


        {/* STEP 4 - Owner Details */}
        {activeStep === 3 && (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <Typography variant="h6" style={{ color: '#667eea', fontWeight: 600 }}>
                👤 Hotel Owner Details
              </Typography>
            </div>

            {/* Owner Name */}
            <div className="enhanced-textfield">
              <TextField
                label="Owner Name"
                name="ownerName"
                value={hotel.hotelOwnerDetails.ownerName}
                onChange={handleOwnerDetailsChange}
                fullWidth
                required
              />
            </div>

            {/* Owner Mobile */}
            <div className="enhanced-textfield">
              <TextField
                label="Owner Mobile Number"
                name="ownerMobile"
                value={hotel.hotelOwnerDetails.ownerMobile}
                onChange={handleOwnerDetailsChange}
                fullWidth
                required
                placeholder="e.g., +977-9812345678"
              />
            </div>

            {/* Owner Email */}
            <div className="enhanced-textfield">
              <TextField
                label="Owner Email"
                name="ownerEmail"
                type="email"
                value={hotel.hotelOwnerDetails.ownerEmail}
                onChange={handleOwnerDetailsChange}
                fullWidth
                required
                placeholder="e.g., owner@example.com"
              />
            </div>

            {/* Owner ID Proof Upload */}
            <div className="border p-4 rounded-lg bg-gray-50">
              <label className="font-semibold block mb-2">Owner ID Proof</label>
              <div className="mb-2">
                <input
                  id="owner-id-input"
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleOwnerIdUpload}
                  style={{ display: 'none' }}
                />
                <label htmlFor="owner-id-input">
                  <Button
                    variant="contained"
                    component="span"
                    disabled={uploadingOwnerId}
                    style={{
                      backgroundColor: uploadingOwnerId ? '#cccccc' : '#667eea',
                      color: 'white',
                      textTransform: 'none',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      cursor: uploadingOwnerId ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {uploadingOwnerId ? '📤 Uploading...' : '📁 Upload ID Proof'}
                  </Button>
                </label>
              </div>
              {hotel.hotelOwnerDetails.ownerIdProof ? (
                <div className="flex items-center gap-2">
                  <a
                    href={hotel.hotelOwnerDetails.ownerIdProof}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm"
                  >
                    ✓ View uploaded document
                  </a>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setHotel(prev => ({
                        ...prev,
                        hotelOwnerDetails: { ...prev.hotelOwnerDetails, ownerIdProof: '' }
                      }));
                      setOwnerIdFile(null);
                      toast.success('Owner ID removed');
                    }}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      width: '24px',
                      height: '24px',
                    }}
                  >
                    <CloseIcon style={{ fontSize: '14px' }} />
                  </IconButton>
                </div>
              ) : (
                <p className="text-sm text-gray-500 mt-1">No document uploaded</p>
              )}
            </div>

            {/* Hotel Registration Document Upload */}
            <div className="border p-4 rounded-lg bg-gray-50">
              <label className="font-semibold block mb-2">Hotel Registration Document</label>
              <div className="mb-2">
                <input
                  id="hotel-reg-doc-input"
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleHotelRegDocUpload}
                  style={{ display: 'none' }}
                />
                <label htmlFor="hotel-reg-doc-input">
                  <Button
                    variant="contained"
                    component="span"
                    disabled={uploadingRegDoc}
                    style={{
                      backgroundColor: uploadingRegDoc ? '#cccccc' : '#667eea',
                      color: 'white',
                      textTransform: 'none',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      cursor: uploadingRegDoc ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {uploadingRegDoc ? '📤 Uploading...' : '📁 Upload Registration Doc'}
                  </Button>
                </label>
              </div>
              {hotel.hotelOwnerDetails.hotelRegistrationDoc ? (
                <div className="flex items-center gap-2">
                  <a
                    href={hotel.hotelOwnerDetails.hotelRegistrationDoc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm"
                  >
                    ✓ View uploaded document
                  </a>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setHotel(prev => ({
                        ...prev,
                        hotelOwnerDetails: { ...prev.hotelOwnerDetails, hotelRegistrationDoc: '' }
                      }));
                      setHotelRegDocFile(null);
                      toast.success('Hotel registration document removed');
                    }}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      width: '24px',
                      height: '24px',
                    }}
                  >
                    <CloseIcon style={{ fontSize: '14px' }} />
                  </IconButton>
                </div>
              ) : (
                <p className="text-sm text-gray-500 mt-1">No document uploaded</p>
              )}
            </div>
          </div>
        )}

        {/* STEP 5 - Description & Rules */}
        {activeStep === 4 && (
          <div className="space-y-4">
            <div className="enhanced-textfield">
              <TextField label="Description" name="description" value={hotel.description} onChange={handleChange} fullWidth multiline minRows={3} required />
            </div>
            <div className="enhanced-textfield">
              <TextField label="Rules & Policies" name="rulesPolicies" value={hotel.rulesPolicies} onChange={handleChange} fullWidth />
            </div>
            <div className="enhanced-textfield">
              <TextField label="Check-in Time" name="check_in" value={hotel.check_in} onChange={handleChange} fullWidth placeholder="e.g., 2:00 PM" />
            </div>
            <div className="enhanced-textfield">
              <TextField label="Check-out Time" name="check_out" value={hotel.check_out} onChange={handleChange} fullWidth placeholder="e.g., 11:00 AM" />
            </div>

            {/* Rating Field */}
            <Box className="rating-section-modern border p-4 rounded-lg">
              <Typography component="legend" className="font-semibold mb-2 text-lg" style={{ color: '#92400e' }}>
                ⭐ Hotel Rating
              </Typography>
              <Box className="flex items-center gap-4">
                <Rating
                  name="hotel-rating"
                  value={hotel.rating || 0}
                  precision={0.5}
                  size="large"
                  onChange={(_, newValue) => {
                    setHotel(prev => ({ ...prev, rating: newValue || 0 }));
                  }}
                />
                {/* <Typography className="text-gray-600">
                  {hotel.rating ? `${hotel.rating} / 5` : 'No rating set'}
                </Typography> */}
              </Box>
            </Box>

            
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-between mt-8 gap-4">
          {activeStep > 0 && (
            <Button
              variant="outlined"
              onClick={handleBack}
              className="modern-button"
              style={{ borderColor: '#667eea', color: '#667eea' }}
            >
              ← Back
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={loading}
            className="modern-button"
            style={{
              marginLeft: activeStep === 0 ? 'auto' : '0',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}
          >
            {activeStep === steps.length - 1 ? (
              loading ? 'Submitting...' : (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Send size={18} />
                  Submit
                </span>
              )
            ) : 'Next →'}
          </Button>
        </div>
      </form>

      {/* Image Modal */}
      <Modal
        open={imageModalOpen}
        onClose={closeImageModal}
        aria-labelledby="image-modal"
        aria-describedby="image-preview-modal"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: '90vw',
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: '16px',
            p: 2,
            outline: 'none',
          }}
        >
          <IconButton
            onClick={closeImageModal}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.7)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '85vh',
                borderRadius: '12px',
                display: 'block',
              }}
            />
          )}
        </Box>
      </Modal>

      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      </div>
    </div>
  );
};

export default OnboardingForm;
